import { IonContent, IonFooter, IonToolbar, IonInput, IonButton, IonIcon, useIonToast, IonHeader, IonButtons, IonBackButton, IonTitle } from '@ionic/react';
import { Box, Typography } from '@mui/material';
import { send, person } from 'ionicons/icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, orderBy, onSnapshot, getDoc, addDoc, updateDoc, doc, Timestamp, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../hooks/useAuth';
import { Message } from '../types/message';
import { BaseUser, Company, Contractor, Ambassador } from '../types/user';

interface ChatProps {
  conversationId: string;
  otherUser: BaseUser;
}

export const Chat: React.FC<ChatProps> = ({ conversationId, otherUser }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [presentToast] = useIonToast();
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [isBlocked, setIsBlocked] = useState(false);
  const [blockDuration, setBlockDuration] = useState(5000); // 5 seconds initial
  const [messageCount, setMessageCount] = useState(0);
  const [lastMessageTime, setLastMessageTime] = useState(Date.now());
  const MAX_MESSAGE_LENGTH = 500;
  const contentRef = useRef<HTMLIonContentElement>(null);

  useEffect(() => {
    if (!user?.uid || !conversationId) return;

    // Subscribe to messages in this conversation
    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('timestamp', 'asc')
    );

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const messagesData: Message[] = [];
      snapshot.forEach((doc) => {
        const message = {
          id: doc.id,
          ...doc.data()
        } as Message;
        messagesData.push(message);
      });
      setMessages(messagesData);
      
      // Mark messages as read and update conversation unread count
      const unreadMessages = snapshot.docChanges().filter(change => 
        change.type === 'added' &&
        change.doc.data().receiverId === user.uid &&
        !change.doc.data().read
      );

      if (unreadMessages.length > 0) {
        // Mark individual messages as read
        await Promise.all(unreadMessages.map(change => 
          updateDoc(doc(db, 'conversations', conversationId, 'messages', change.doc.id), {
            read: true
          })
        ));

        // Reset conversation unread count
        const conversationRef = doc(db, 'conversations', conversationId);
        await updateDoc(conversationRef, {
          [`unreadCount.${user.uid}`]: 0
        });
      }
    });

    return () => unsubscribe();
  }, [conversationId, user?.uid]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    contentRef.current?.scrollToBottom(300);
  }, [messages]);

  const sendMessage = async () => {
    if (!user?.uid || !newMessage.trim() || isBlocked) return;
    if (newMessage.length > MAX_MESSAGE_LENGTH) return;

    // Check if user is trying to message themselves
    if (user.uid === otherUser.id) {
      presentToast({
        message: t('messages.cannot_message_self'),
        duration: 2000,
        color: 'danger'
      });
      return;
    }

    const now = Date.now();
    const timeSinceLastMessage = now - lastMessageTime;

    // Increment message count if less than 10 seconds have passed
    if (timeSinceLastMessage < 10000) {
      setMessageCount(prev => prev + 1);
    } else {
      setMessageCount(1);
    }

    // Check for spam (10 messages in less than 10 seconds)
    if (messageCount >= 9 && timeSinceLastMessage < 10000) {
      setIsBlocked(true);
      setTimeout(() => {
        setIsBlocked(false);
        setBlockDuration(prev => prev * 1.5); // Increase block duration by 50%
      }, blockDuration);
      return;
    }

    setLastMessageTime(now);
    
    // Store message content before clearing the input
    const messageContent = newMessage.trim();
    // Clear the input immediately to improve user experience
    setNewMessage('');

    try {
      // Verify the conversation exists and user is a participant
      const conversationRef = doc(db, 'conversations', conversationId);
      const conversationSnap = await getDoc(conversationRef);
      
      if (!conversationSnap.exists()) {
        presentToast({
          message: t('messages.conversation_not_found'),
          duration: 2000,
          color: 'danger'
        });
        return;
      }
      
      const conversationData = conversationSnap.data();
      if (!conversationData.participants.includes(user.uid)) {
        presentToast({
          message: t('messages.not_participant'),
          duration: 2000,
          color: 'danger'
        });
        return;
      }

      // Add message to the conversation
      await addDoc(collection(db, 'conversations', conversationId, 'messages'), {
        content: messageContent,
        senderId: user.uid,
        receiverId: otherUser.id,
        timestamp: Timestamp.now(),
        read: false
      });
      
      // Increment unread count for receiver
      const currentUnreadCount = ((conversationData?.unreadCount || {})[otherUser.id] || 0);
      
      const updates = {
        lastMessage: {
          content: messageContent,
          timestamp: Timestamp.now()
        },
        updatedAt: Timestamp.now()
      };

      // Only update unread count if it's a new message
      if (!conversationData?.lastMessage || 
          conversationData.lastMessage.content !== messageContent) {
        updates[`unreadCount.${otherUser.id}`] = currentUnreadCount + 1;
      }
      
      await updateDoc(conversationRef, updates);

      // Create notification for new message
      try {
        // Get current user's data
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const userData = userDoc.data() as BaseUser;
        let userName = 'Unknown User';

        if (userData.type === 'company') {
          userName = (userData as Company).companyName;
        } else if (userData.type === 'contractor' || userData.type === 'ambassador') {
          const userWithName = userData as Contractor | Ambassador;
          userName = `${userWithName.firstName} ${userWithName.lastName}`;
        }

        // Always create notification without checking recipient's settings
        // This avoids permission issues when trying to read other user's settings
        await addDoc(collection(db, 'notifications'), {
          type: 'message',
          fromUserId: user.uid,
          fromUserName: userName,
          toUserId: otherUser.id,
          read: false,
          createdAt: serverTimestamp()
        });
      } catch (notificationError) {
        // If notification creation fails, we still want the message to be sent
        console.error('Error creating notification:', notificationError);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      presentToast({
        message: t('messages.send_error'),
        duration: 2000,
        color: 'danger'
      });
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  return (
    <>
      <IonContent ref={contentRef} className="ion-padding">
        <div className="messages-container">
          {messages.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {t('messages.empty_conversation')}
              </Typography>
            </Box>
          ) : (
            messages.map((message) => {
              const isSent = message.senderId === user?.uid;
              return (
                <div
                  key={message.id}
                  className={`message ${isSent ? 'sent' : 'received'}`}
                >
                  <div className="message-content">
                    <p>{message.content}</p>
                    <span className="timestamp">
                      {new Date(message.timestamp.toDate()).toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </IonContent>
      <IonFooter keyboard-attach>
        <IonToolbar>
          <div className="message-input-container">
            <IonInput
              type="text"
              value={newMessage}
              onIonInput={e => setNewMessage(e.detail.value || '')}
              onKeyDown={handleKeyPress}
              placeholder={isBlocked ? `Message blocked for ${Math.ceil(blockDuration/1000)}s` : t('messages.type_message')}
              className="message-input"
              maxlength={MAX_MESSAGE_LENGTH}
              disabled={isBlocked}
              inputmode="text"
              enterkeyhint="send"
              autocapitalize="sentences"
              autocorrect="on"
              spellcheck={true}
            />
            <div className="message-input-counter">
              {newMessage.length}/{MAX_MESSAGE_LENGTH}
            </div>
            <IonButton
              fill="solid"
              onClick={sendMessage}
              disabled={!newMessage.trim() || isBlocked}
              className="send-button"
            >
              <IonIcon icon={send} slot="icon-only" />
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </>
  );
};
